import type { Partner } from "@expert/shared-types";
import { getFeature } from "@soluto-private/expert-workspace-feature-flagging";
import { env } from "@/config";
import { userCache } from "@/shared-utils/auth";
import { useFeatureFlag } from "@/shared-utils/hooks/useFeatureFlag";

export const CHAT_EXPERT_ASSIST_FEATURE_FLAG = "isChatExpertAssistEnabled";
export const CHAT_EXPERT_ASSIST_CACHE_TTL = 1800; // 30 minutes

export interface ChatExpertAssistContext {
    partner?: Partner;
    topic?: string;
}

/**
 * Creates standardized context object for the chat expert assist feature flag so our usage in multiple files is consistent
 */
export const createChatExpertAssistContext = ({ partner, topic }: ChatExpertAssistContext) => ({
    environment: env.mode,
    channel: env.channel,
    employeeId: userCache.employeeId ?? userCache.identity,
    appName: "expert-workspace",
    source: "expert-ui",
    partner,
    topic,
});

export const useChatExpertAssistEnabled = (context: ChatExpertAssistContext) => {
    return useFeatureFlag(CHAT_EXPERT_ASSIST_FEATURE_FLAG, createChatExpertAssistContext(context), {
        cacheTtl: CHAT_EXPERT_ASSIST_CACHE_TTL,
    });
};

/**
 * Direct async call to get chat expert assist feature flag (for use in subscriptions/effects)
 * Returns false by default if the feature flag is not set or undefined
 */
export const getChatExpertAssistEnabled = async (context: ChatExpertAssistContext): Promise<boolean> => {
    const result = await getFeature<boolean>(
        CHAT_EXPERT_ASSIST_FEATURE_FLAG,
        createChatExpertAssistContext(context),
        CHAT_EXPERT_ASSIST_CACHE_TTL,
    );
    return result ?? false;
};
